// Global Instructions Rule Applied!
//
// Configuration → Email Categories admin models.
//
// Shape mirrors public.view__email_categories_admin. Two editable collections:
//   * categories  — the 11-row taxonomy (cfg.email_categories): precedence,
//                   labels, the Outlook name + colour write-back stamps
//   * app_config  — the categorization settings (cfg.app_config group
//                   'email_categorization'), including the write-back master switch
//
// There is no create/delete anywhere in this model on purpose: category_key
// drives the hardcoded predicates inside email_classification__classify_event,
// so a category invented in the UI would have no predicate and could never
// match an email. Disabling is the supported way to take one out of play.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const EmailCategoryAdminRowSchema = z
  .object({
    email_categories__id: z.string(),
    // Immutable — what the resolver's predicates are keyed on.
    email_categories__category_key: z.string(),
    email_categories__label: z.string(),
    email_categories__description: z.string().nullish(),
    // Lower wins. The final category is the min-precedence ENABLED row among
    // every predicate that matched.
    email_categories__precedence: z.number(),
    // deterministic | hybrid | llm | fallback. Read-only: it describes how the
    // code resolves the category, not a setting.
    email_categories__derivation: z.string(),
    // What Power Automate stamps — comparison only, never written by us.
    email_categories__automate_label: z.string().nullish(),
    // What Reservoir stamps. Empty falls back to automate_label, then label.
    email_categories__outlook_label: z.string().nullish(),
    email_categories__outlook_color: z.string().nullish(),
    // The fallback already applied by the RPC, so the panel and the writer can
    // never disagree about which name lands in Outlook.
    email_categories__effective_outlook_label: z.string().nullish(),
    email_categories__enabled: z.boolean().nullish(),
    email_categories__classified_count_30d: z.number().nullish()
  })
  .strict()

export type EmailCategoryAdminRow = z.infer<typeof EmailCategoryAdminRowSchema>

/** One categorization setting. The RPC emits every whitelisted key whether or
 *  not a cfg row exists, so an unseeded key renders as "code default". */
export const EmailCategorySettingSchema = z
  .object({
    app_config__id: z.string().nullish(),
    app_config__config_key: z.string(),
    // jsonb: boolean, number, or the declined-status string list.
    app_config__value_json: z.any(),
    app_config__environment: z.string().nullish(),
    app_config__description: z.string().nullish(),
    app_config__is_environment_override: z.boolean().nullish(),
    app_config__exists: z.boolean().nullish()
  })
  .strict()

export type EmailCategorySetting = z.infer<typeof EmailCategorySettingSchema>

/** A recipient marker — an Outlook category stamped ALONGSIDE the content
 *  category to record how the mailbox was addressed. A separate dimension from
 *  the taxonomy: additive rather than mutually exclusive, so a message can be
 *  "Pipeline" AND "To". Update-only for the same reason as the taxonomy —
 *  marker_key drives the To-beats-Cc resolution inside the enqueue. */
export const EmailRecipientMarkerSchema = z
  .object({
    email_recipient_markers__id: z.string(),
    // Immutable: 'to' | 'cc'.
    email_recipient_markers__marker_key: z.string(),
    email_recipient_markers__label: z.string(),
    email_recipient_markers__outlook_color: z.string().nullish(),
    email_recipient_markers__enabled: z.boolean(),
    email_recipient_markers__description: z.string().nullish()
  })
  .strict()

export type EmailRecipientMarker = z.infer<typeof EmailRecipientMarkerSchema>

export interface EmailCategoriesConfigAdminData {
  /** Which environment layer the settings resolved from ('global' unless an
   *  environment-specific row shadows it). */
  resolvedEnvironment: string
  categories: EmailCategoryAdminRow[]
  settings: EmailCategorySetting[]
  recipientMarkers: EmailRecipientMarker[]
}

/** Microsoft's fixed category palette (Graph outlookCategory `color`). These
 *  are NOT free-form colours — Outlook renders preset0..preset24 and nothing
 *  else, so the picker is a closed list and the hexes below only approximate
 *  what Outlook draws, for the swatch. */
export const OUTLOOK_PRESETS: Array<{ value: string; label: string; hex: string }> = [
  { value: 'preset0', label: 'Red', hex: '#e74856' },
  { value: 'preset1', label: 'Orange', hex: '#ff8c00' },
  { value: 'preset2', label: 'Brown', hex: '#a0522d' },
  { value: 'preset3', label: 'Yellow', hex: '#fff100' },
  { value: 'preset4', label: 'Green', hex: '#16c60c' },
  { value: 'preset5', label: 'Teal', hex: '#00b7c3' },
  { value: 'preset6', label: 'Olive', hex: '#a4c400' },
  { value: 'preset7', label: 'Blue', hex: '#0078d7' },
  { value: 'preset8', label: 'Purple', hex: '#8764b8' },
  { value: 'preset9', label: 'Cranberry', hex: '#c30052' },
  { value: 'preset10', label: 'Steel', hex: '#69797e' },
  { value: 'preset11', label: 'Dark steel', hex: '#4a5459' },
  { value: 'preset12', label: 'Gray', hex: '#9e9e9e' },
  { value: 'preset13', label: 'Dark gray', hex: '#616161' },
  { value: 'preset14', label: 'Black', hex: '#000000' },
  { value: 'preset15', label: 'Dark red', hex: '#a4262c' },
  { value: 'preset16', label: 'Dark orange', hex: '#d83b01' },
  { value: 'preset17', label: 'Dark brown', hex: '#6d4c41' },
  { value: 'preset18', label: 'Dark yellow', hex: '#c19c00' },
  { value: 'preset19', label: 'Dark green', hex: '#107c10' },
  { value: 'preset20', label: 'Dark teal', hex: '#038387' },
  { value: 'preset21', label: 'Dark olive', hex: '#6e8b3d' },
  { value: 'preset22', label: 'Dark blue', hex: '#004e8c' },
  { value: 'preset23', label: 'Dark purple', hex: '#5c2e91' },
  { value: 'preset24', label: 'Dark cranberry', hex: '#750b1c' }
]

export function validateEmailCategoryAdminRow(
  data: unknown,
  options?: { isArray?: boolean }
): EmailCategoryAdminRow | EmailCategoryAdminRow[] {
  try {
    return validateWithZodSchema(
      EmailCategoryAdminRowSchema,
      data,
      'email category',
      options
    ) as EmailCategoryAdminRow | EmailCategoryAdminRow[]
  } catch (e) {
    throw new Error(`Email category validation failed: ${e}`)
  }
}

export function validateEmailRecipientMarker(
  data: unknown,
  options?: { isArray?: boolean }
): EmailRecipientMarker | EmailRecipientMarker[] {
  try {
    return validateWithZodSchema(
      EmailRecipientMarkerSchema,
      data,
      'email recipient marker',
      options
    ) as EmailRecipientMarker | EmailRecipientMarker[]
  } catch (e) {
    throw new Error(`Email recipient marker validation failed: ${e}`)
  }
}

export function validateEmailCategorySetting(
  data: unknown,
  options?: { isArray?: boolean }
): EmailCategorySetting | EmailCategorySetting[] {
  try {
    return validateWithZodSchema(
      EmailCategorySettingSchema,
      data,
      'email categorization setting',
      options
    ) as EmailCategorySetting | EmailCategorySetting[]
  } catch (e) {
    throw new Error(`Email categorization setting validation failed: ${e}`)
  }
}
